import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { Card, FakeText, IconButton } from '@totalsoft/rocket-ui';
import { ADD_UPDATE_SCHEDULE_TYPE_CELL_PARAM_TEMPLATE } from '../../mutations';
import { SCHEDULE_TYPE_CELL_PARAM_TEMPLATE_UPDATED } from '../../subscriptions';
import { SCHEDULE_TYPES_WITH_CELL_PARAMS } from '../../queries';
import { useToast } from 'hooks/toasts/useToast';
import { get } from '@totalsoft/change-tracking-react';
import { emptyArray } from 'utils/constants';
import { isValid } from '@totalsoft/pure-validations-react';
import { isDirty } from '@totalsoft/change-tracking';
import ViewList from '@mui/icons-material/ViewList';
import ScheduleTypeCellParamTemplateHeaderComponent from './ScheduleTypeCellParamTemplateHeaderComponent';
import { omit } from 'ramda';

const ScheduleTypeCellParamTemplateHeaderContainer = ({
  columns,
  scheduleTypeCellParamTemplateId,
  onRedirect,
  lens,
  dirtyInfo,
  loading,
  saving,
  setSaving,
  scheduleTypeCellParamTemplateValidation,
  validateScheduleTypeCellParamTemplate,
  canWrite
}) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const { data: scheduleTypesData } = useQuery(SCHEDULE_TYPES_WITH_CELL_PARAMS);

  const [addOrUpdateScheduleTypeCellParamTemplate] = useMutation(ADD_UPDATE_SCHEDULE_TYPE_CELL_PARAM_TEMPLATE, {
    onError: () => {
      setSaving(false);
    }
  });

  useSubscription(SCHEDULE_TYPE_CELL_PARAM_TEMPLATE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ScheduleTypeCellParamTemplate.ScheduleTypeCellParamTemplateUpdated'), 'success');
      const id = data.scheduleTypeCellParamTemplateUpdated.scheduleTypeCellParamTemplate.scheduleTypeCellParamTemplateId;
      if (scheduleTypeCellParamTemplateId === undefined) onRedirect(id);
      setSaving(false);
      client.cache.evict({ id: `ScheduleTypeCellParamTemplateItem:{"scheduleTypeCellParamTemplateId":${id}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedScheduleTypeCellParamTemplates' });
      client.cache.gc();
    }
  });

  const handleSave = useCallback(() => {
    const input = { ...(lens |> get), variableColumns: columns?.scheduleTypeCellParamTemplateColumns };
    if (!validateScheduleTypeCellParamTemplate(input)) return;
    setSaving(true);
    input.scheduleTypeCellParamTemplateDetails = input.scheduleTypeCellParamTemplateDetails.map(detail => omit(['Selected'], detail));
    addOrUpdateScheduleTypeCellParamTemplate({ variables: { input } });
  }, [addOrUpdateScheduleTypeCellParamTemplate, columns, lens, setSaving, validateScheduleTypeCellParamTemplate]);

  useEffect(() => {
    validateScheduleTypeCellParamTemplate(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateScheduleTypeCellParamTemplate]);

  return (
    <Card
      icon={ViewList}
      iconColor='primary'
      title={t('ScheduleTypeCellParamTemplate.GeneralData')}
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('SaveChanges')}
          onClick={handleSave}
          disabled={!isDirty(dirtyInfo) || saving || !(scheduleTypeCellParamTemplateValidation |> isValid) || !canWrite}
        />
      }
    >
      <Prompt
        when={scheduleTypeCellParamTemplateId !== undefined && isDirty(dirtyInfo)}
        message={t('ScheduleTypeCellParamTemplate.LeavingWithoutSavingScheduleTypeCellParamTemplate')}
      />
      {loading ? (
        <FakeText lines={15} />
      ) : (
        <ScheduleTypeCellParamTemplateHeaderComponent
          scheduleTypeCellParamTemplateLens={lens}
          scheduleTypes={scheduleTypesData?.scheduleTypesWithCellParams ?? emptyArray}
          validation={scheduleTypeCellParamTemplateValidation}
          canWrite={canWrite}
        />
      )}
    </Card>
  );
};

ScheduleTypeCellParamTemplateHeaderContainer.propTypes = {
  columns: PropTypes.array.isRequired,
  scheduleTypeCellParamTemplateId: PropTypes.number,
  onRedirect: PropTypes.func,
  lens: PropTypes.object.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  setSaving: PropTypes.func.isRequired,
  scheduleTypeCellParamTemplateValidation: PropTypes.object.isRequired,
  validateScheduleTypeCellParamTemplate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired
};

export default ScheduleTypeCellParamTemplateHeaderContainer;
